/**
 * Edit Job Dialog Component
 */

import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { CalendarIcon, Loader2, Plus, Trash2, GripVertical, ChevronDown, ChevronUp, Check, ChevronsUpDown } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useUpdateInstallationJob, useTemplates, useAccountQuotes } from '../hooks/useInstallationJobs';
import type { InstallationJob, JobStatus, ChecklistItem, ChecklistItemType } from '../types';

interface EditJobDialogProps {
  job: InstallationJob;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const STATUS_OPTIONS: { value: JobStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'pending_review', label: 'Pending Review' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const CHECKLIST_ITEM_TYPES: { value: ChecklistItemType; label: string }[] = [
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'text', label: 'Text Input' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'photo', label: 'Photo' },
  { value: 'signature', label: 'Signature' },
  { value: 'dropdown', label: 'Dropdown' },
];

interface EditableChecklistItem {
  id: string;
  key: string;
  displayName: string;
  type: ChecklistItemType;
  required: boolean;
  sortOrder: number;
  options?: Record<string, unknown>;
  dropdownOptions?: string; // comma-separated for dropdown type
}

function generateId(): string {
  return `item_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

export function EditJobDialog({ job, open, onOpenChange }: EditJobDialogProps) {
  const updateJob = useUpdateInstallationJob();
  const { data: templatesData } = useTemplates();
  const { data: quotesData } = useAccountQuotes(job.accountId);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    instructions: '',
    status: 'draft' as JobStatus,
    scheduledDate: undefined as Date | undefined,
    scheduledTimeStart: '',
    scheduledTimeEnd: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    address: '',
    city: '',
    stateProvince: '',
    postalCode: '',
    country: '',
    mongoQuoteId: '' as string,
  });

  const [quoteComboboxOpen, setQuoteComboboxOpen] = useState(false);
  const [checklistItems, setChecklistItems] = useState<EditableChecklistItem[]>([]);
  const [isChecklistOpen, setIsChecklistOpen] = useState(false);

  // Reset form when dialog opens with job data
  useEffect(() => {
    if (open && job) {
      setFormData({
        title: job.title || '',
        description: job.description || '',
        instructions: job.instructions || '',
        status: job.status,
        scheduledDate: job.scheduledDate ? new Date(job.scheduledDate) : undefined,
        scheduledTimeStart: job.scheduledTimeStart || '',
        scheduledTimeEnd: job.scheduledTimeEnd || '',
        contactName: job.contactName || '',
        contactPhone: job.contactPhone || '',
        contactEmail: job.contactEmail || '',
        address: job.address || '',
        city: job.city || '',
        stateProvince: job.stateProvince || '',
        postalCode: job.postalCode || '',
        country: job.country || '',
        mongoQuoteId: job.mongoQuoteId || '',
      });

      // Convert existing checklist items to editable format
      const editableItems: EditableChecklistItem[] = (job.checklist || []).map((item) => ({
        id: item.id,
        key: item.key,
        displayName: item.displayName,
        type: item.type,
        required: item.required,
        sortOrder: item.sortOrder,
        options: item.options,
        dropdownOptions:
          item.type === 'dropdown' && item.options?.choices
            ? (item.options.choices as string[]).join(', ')
            : '',
      }));
      setChecklistItems(editableItems);
      setIsChecklistOpen(editableItems.length > 0);
    }
  }, [open, job]);

  const handleAddChecklistItem = () => {
    const newItem: EditableChecklistItem = {
      id: generateId(),
      key: `item_${checklistItems.length + 1}`,
      displayName: '',
      type: 'checkbox',
      required: false,
      sortOrder: checklistItems.length,
      dropdownOptions: '',
    };
    setChecklistItems([...checklistItems, newItem]);
  };

  const handleRemoveChecklistItem = (id: string) => {
    setChecklistItems(checklistItems.filter((item) => item.id !== id));
  };

  const handleUpdateChecklistItem = (id: string, updates: Partial<EditableChecklistItem>) => {
    setChecklistItems(
      checklistItems.map((item) => (item.id === id ? { ...item, ...updates } : item))
    );
  };

  const handleMoveChecklistItem = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === checklistItems.length - 1)
    ) {
      return;
    }

    const newItems = [...checklistItems];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [newItems[index], newItems[swapIndex]] = [newItems[swapIndex], newItems[index]];

    // Update sort orders
    newItems.forEach((item, i) => {
      item.sortOrder = i;
    });

    setChecklistItems(newItems);
  };

  const handleApplyTemplate = (templateId: string) => {
    const template = templatesData?.templates.find((t) => t.id === templateId);
    if (!template) return;

    const templateItems: EditableChecklistItem[] = template.checklistItems.map((item, index) => ({
      id: generateId(),
      key: item.key,
      displayName: item.displayName,
      type: item.type,
      required: item.required,
      sortOrder: checklistItems.length + index,
      options: item.options,
      dropdownOptions:
        item.type === 'dropdown' && item.options?.choices
          ? (item.options.choices as string[]).join(', ')
          : '',
    }));

    setChecklistItems([...checklistItems, ...templateItems]);
    toast.success(`Added ${templateItems.length} items from template`);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    // Convert editable checklist items to API format
    const checklist: ChecklistItem[] = checklistItems
      .filter((item) => item.displayName.trim()) // Only include items with a name
      .map((item, index) => ({
        id: item.id,
        key: item.key || `item_${index + 1}`,
        displayName: item.displayName.trim(),
        type: item.type,
        required: item.required,
        sortOrder: index,
        options:
          item.type === 'dropdown' && item.dropdownOptions
            ? {
                choices: item.dropdownOptions
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean),
              }
            : item.options,
      }));

    try {
      await updateJob.mutateAsync({
        id: job.id,
        input: {
          title: formData.title.trim(),
          description: formData.description.trim() || undefined,
          instructions: formData.instructions.trim() || undefined,
          status: formData.status,
          scheduledDate: formData.scheduledDate?.toISOString().split('T')[0],
          scheduledTimeStart: formData.scheduledTimeStart || undefined,
          scheduledTimeEnd: formData.scheduledTimeEnd || undefined,
          contactName: formData.contactName.trim() || undefined,
          contactPhone: formData.contactPhone.trim() || undefined,
          contactEmail: formData.contactEmail.trim() || undefined,
          address: formData.address.trim() || undefined,
          city: formData.city.trim() || undefined,
          stateProvince: formData.stateProvince.trim() || undefined,
          postalCode: formData.postalCode.trim() || undefined,
          country: formData.country.trim() || undefined,
          checklist,
          mongoQuoteId: formData.mongoQuoteId || null,
        },
      });
      toast.success('Job updated successfully');
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to update job');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit Installation Job</DialogTitle>
            <DialogDescription>Update job details for {job.jobNumber}</DialogDescription>
          </DialogHeader>

          <div className="grid gap-6 py-4">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground">Basic Information</h3>

              <div className="grid gap-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="Installation job title"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, description: e.target.value }))
                  }
                  placeholder="Brief description of the job"
                  rows={2}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="instructions">Instructions</Label>
                <Textarea
                  id="instructions"
                  value={formData.instructions}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, instructions: e.target.value }))
                  }
                  placeholder="Detailed instructions for the technician"
                  rows={4}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value: JobStatus) =>
                    setFormData((prev) => ({ ...prev, status: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="quote">Quote</Label>
                {job.accountId ? (
                  <Popover open={quoteComboboxOpen} onOpenChange={setQuoteComboboxOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={quoteComboboxOpen}
                        className="w-full justify-between font-normal"
                      >
                        {formData.mongoQuoteId
                          ? (() => {
                              const q = quotesData?.quotes?.find((q) => (q.mongoQuoteId || q.id) === formData.mongoQuoteId);
                              return q ? `${q.quoteNumber}${q.projectName ? ` — ${q.projectName}` : ''}` : 'Select a quote...';
                            })()
                          : 'No quote'}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search quotes..." />
                        <CommandList>
                          <CommandEmpty>No quote found.</CommandEmpty>
                          <CommandGroup>
                            <CommandItem
                              value="no-quote"
                              onSelect={() => {
                                setFormData((prev) => ({ ...prev, mongoQuoteId: '' }));
                                setQuoteComboboxOpen(false);
                              }}
                            >
                              <Check className={cn("mr-2 h-4 w-4", !formData.mongoQuoteId ? "opacity-100" : "opacity-0")} />
                              No quote
                            </CommandItem>
                            {quotesData?.quotes?.map((quote) => (
                              <CommandItem
                                key={quote.id}
                                value={`${quote.quoteNumber} ${quote.projectName || ''}`}
                                onSelect={() => {
                                  setFormData((prev) => ({ ...prev, mongoQuoteId: quote.mongoQuoteId || quote.id }));
                                  setQuoteComboboxOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    formData.mongoQuoteId === (quote.mongoQuoteId || quote.id) ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                <div className="flex flex-col">
                                  <span>{quote.quoteNumber}{quote.projectName ? ` — ${quote.projectName}` : ''}</span>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                ) : (
                  <p className="text-sm text-muted-foreground py-2">Set an account first to link a quote</p>
                )}
              </div>
            </div>

            {/* Schedule */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground">Schedule</h3>

              <div className="grid gap-2">
                <Label>Scheduled Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'justify-start text-left font-normal',
                        !formData.scheduledDate && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.scheduledDate ? (
                        format(formData.scheduledDate, 'PPP')
                      ) : (
                        <span>Pick a date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.scheduledDate}
                      onSelect={(date) =>
                        setFormData((prev) => ({ ...prev, scheduledDate: date }))
                      }
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="scheduledTimeStart">Start Time</Label>
                  <Input
                    id="scheduledTimeStart"
                    type="time"
                    value={formData.scheduledTimeStart}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, scheduledTimeStart: e.target.value }))
                    }
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="scheduledTimeEnd">End Time</Label>
                  <Input
                    id="scheduledTimeEnd"
                    type="time"
                    value={formData.scheduledTimeEnd}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, scheduledTimeEnd: e.target.value }))
                    }
                  />
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground">Contact Information</h3>

              <div className="grid gap-2">
                <Label htmlFor="contactName">Contact Name</Label>
                <Input
                  id="contactName"
                  value={formData.contactName}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, contactName: e.target.value }))
                  }
                  placeholder="On-site contact name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="contactPhone">Contact Phone</Label>
                  <Input
                    id="contactPhone"
                    type="tel"
                    value={formData.contactPhone}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, contactPhone: e.target.value }))
                    }
                    placeholder="Phone number"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="contactEmail">Contact Email</Label>
                  <Input
                    id="contactEmail"
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, contactEmail: e.target.value }))
                    }
                    placeholder="Email address"
                  />
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground">Address</h3>

              <div className="grid gap-2">
                <Label htmlFor="address">Street Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))}
                  placeholder="Street address"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData((prev) => ({ ...prev, city: e.target.value }))}
                    placeholder="City"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="stateProvince">State/Province</Label>
                  <Input
                    id="stateProvince"
                    value={formData.stateProvince}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, stateProvince: e.target.value }))
                    }
                    placeholder="State/Province"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="postalCode">Postal Code</Label>
                  <Input
                    id="postalCode"
                    value={formData.postalCode}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, postalCode: e.target.value }))
                    }
                    placeholder="Postal code"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => setFormData((prev) => ({ ...prev, country: e.target.value }))}
                    placeholder="Country"
                  />
                </div>
              </div>
            </div>

            {/* Checklist */}
            <Collapsible open={isChecklistOpen} onOpenChange={setIsChecklistOpen}>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" className="p-0 h-auto hover:bg-transparent">
                      <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                        Checklist ({checklistItems.length} items)
                        {isChecklistOpen ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </h3>
                    </Button>
                  </CollapsibleTrigger>

                  <div className="flex gap-2">
                    {templatesData && templatesData.templates.length > 0 && (
                      <Select onValueChange={handleApplyTemplate}>
                        <SelectTrigger className="w-[180px] h-8 text-sm">
                          <SelectValue placeholder="Apply template" />
                        </SelectTrigger>
                        <SelectContent>
                          {templatesData.templates
                            .filter((t) => t.isActive)
                            .map((template) => (
                              <SelectItem key={template.id} value={template.id}>
                                {template.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAddChecklistItem}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Item
                    </Button>
                  </div>
                </div>

                <CollapsibleContent className="space-y-3">
                  {checklistItems.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4 border rounded-md">
                      No checklist items. Add items or apply a template.
                    </p>
                  ) : (
                    checklistItems.map((item, index) => (
                      <div
                        key={item.id}
                        className="border rounded-md p-3 space-y-3 bg-muted/30"
                      >
                        <div className="flex items-start gap-2">
                          <div className="flex flex-col gap-1 pt-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => handleMoveChecklistItem(index, 'up')}
                              disabled={index === 0}
                            >
                              <ChevronUp className="h-3 w-3" />
                            </Button>
                            <GripVertical className="h-4 w-4 text-muted-foreground mx-auto" />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => handleMoveChecklistItem(index, 'down')}
                              disabled={index === checklistItems.length - 1}
                            >
                              <ChevronDown className="h-3 w-3" />
                            </Button>
                          </div>

                          <div className="flex-1 space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div className="grid gap-1">
                                <Label className="text-xs">Label *</Label>
                                <Input
                                  value={item.displayName}
                                  onChange={(e) =>
                                    handleUpdateChecklistItem(item.id, {
                                      displayName: e.target.value,
                                    })
                                  }
                                  placeholder="Item label"
                                  className="h-8"
                                />
                              </div>
                              <div className="grid gap-1">
                                <Label className="text-xs">Type</Label>
                                <Select
                                  value={item.type}
                                  onValueChange={(value: ChecklistItemType) =>
                                    handleUpdateChecklistItem(item.id, { type: value })
                                  }
                                >
                                  <SelectTrigger className="h-8">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {CHECKLIST_ITEM_TYPES.map((type) => (
                                      <SelectItem key={type.value} value={type.value}>
                                        {type.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            {item.type === 'dropdown' && (
                              <div className="grid gap-1">
                                <Label className="text-xs">Options (comma-separated)</Label>
                                <Input
                                  value={item.dropdownOptions || ''}
                                  onChange={(e) =>
                                    handleUpdateChecklistItem(item.id, {
                                      dropdownOptions: e.target.value,
                                    })
                                  }
                                  placeholder="Option 1, Option 2, Option 3"
                                  className="h-8"
                                />
                              </div>
                            )}

                            <div className="flex items-center gap-2">
                              <Checkbox
                                id={`required-${item.id}`}
                                checked={item.required}
                                onCheckedChange={(checked) =>
                                  handleUpdateChecklistItem(item.id, {
                                    required: checked === true,
                                  })
                                }
                              />
                              <Label htmlFor={`required-${item.id}`} className="text-xs">
                                Required
                              </Label>
                            </div>
                          </div>

                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => handleRemoveChecklistItem(item.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </CollapsibleContent>
              </div>
            </Collapsible>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={updateJob.isPending}>
              {updateJob.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
